import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, buildCombinedReason } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

function formatAccessType(data) {
  if (data.accessType !== 'Synthetic Graft') return data.accessType;
  const material = data.graftMaterial === 'Other' ? data.graftMaterialOther : data.graftMaterial;
  return material ? `Synthetic Graft (${material})` : 'Synthetic Graft';
}

function formatConfiguration(data) {
  return data.configuration === 'Other' && data.configurationOther
    ? data.configurationOther
    : data.configuration;
}

function formatIndication(data) {
  return data.indication === 'Other' && data.indicationOther
    ? data.indicationOther
    : data.indication;
}

export function DialysisAccessPDF({ data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);

  const inflow = data.inflow || {};
  const anastomosis = data.anastomosis || {};
  const body = data.body || {};
  const outflow = data.outflow || {};
  const complications = data.complications || {};
  const stenoses = complications.stenoses || [];
  const thrombi = complications.thrombi || [];
  const aneurysms = complications.aneurysms || [];

  const measurementsText = [
    inflow.psv ? `Inflow PSV: ${inflow.psv} cm/s` : null,
    inflow.stenosisPresent && inflow.stenosisPSV ? `Inflow stenosis PSV: ${inflow.stenosisPSV} cm/s` : null,
    anastomosis.diameter ? `Anastomosis diameter: ${anastomosis.diameter} mm` : null,
    anastomosis.psv ? `Anastomosis PSV: ${anastomosis.psv} cm/s` : null,
    body.proximalDiameter ? `Body proximal: ${body.proximalDiameter} mm, PSV ${body.proximalPSV || '—'} cm/s` : null,
    body.midDiameter ? `Body mid: ${body.midDiameter} mm, PSV ${body.midPSV || '—'} cm/s` : null,
    body.distalDiameter ? `Body distal: ${body.distalDiameter} mm, PSV ${body.distalPSV || '—'} cm/s` : null,
    outflow.psv ? `Outflow PSV: ${outflow.psv} cm/s` : null,
    outflow.stenosisPresent && outflow.stenosisPSV ? `Outflow stenosis PSV: ${outflow.stenosisPSV} cm/s` : null,
  ].filter(Boolean).join('\n');

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="DIALYSIS ACCESS ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <PdfMeasurementBox title="MEASUREMENTS">
              <Text style={commonStyles.infoBoxText}>{measurementsText || 'None recorded'}</Text>
            </PdfMeasurementBox>
            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            <PdfAccentSectionHeader title="ACCESS INFORMATION" />
            <PdfLabelValue label="Arm:" value={data.arm || ''} forceBlack />
            <PdfLabelValue label="Type:" value={formatAccessType(data)} forceBlack />
            <PdfLabelValue label="Configuration:" value={formatConfiguration(data)} forceBlack />
            <PdfLabelValue label="Indication:" value={formatIndication(data)} forceBlack />

            <PdfAccentSectionHeader title="PATENCY" />
            <PdfLabelValue label="Status:" value={data.patency || ''} isAbnormal={data.patency && data.patency !== 'Patent'} />

            <PdfAccentSectionHeader title="INFLOW (FEEDING ARTERY)" />
            <PdfLabelValue label="PSV:" value={inflow.psv ? `${inflow.psv} cm/s` : ''} forceBlack />
            <PdfLabelValue label="Waveform:" value={inflow.waveform || ''} forceBlack />
            <PdfLabelValue label="Focal stenosis:" value={inflow.stenosisPresent ? 'Present' : 'None'} isAbnormal={inflow.stenosisPresent} />
            {inflow.stenosisPresent && inflow.stenosisPSV && (
              <PdfLabelValue label="Stenosis PSV:" value={`${inflow.stenosisPSV} cm/s`} isAbnormal />
            )}

            <PdfAccentSectionHeader title="ANASTOMOSIS" />
            <PdfLabelValue label="PSV:" value={anastomosis.psv ? `${anastomosis.psv} cm/s` : ''} forceBlack />
            <PdfLabelValue label="Diameter:" value={anastomosis.diameter ? `${anastomosis.diameter} mm` : ''} forceBlack />

            <PdfAccentSectionHeader title="BODY OF FISTULA / GRAFT" />
            <PdfLabelValue label="Proximal:" value={body.proximalDiameter ? `${body.proximalDiameter} mm, PSV ${body.proximalPSV || '—'} cm/s` : ''} forceBlack />
            <PdfLabelValue label="Mid:" value={body.midDiameter ? `${body.midDiameter} mm, PSV ${body.midPSV || '—'} cm/s` : ''} forceBlack />
            <PdfLabelValue label="Distal:" value={body.distalDiameter ? `${body.distalDiameter} mm, PSV ${body.distalPSV || '—'} cm/s` : ''} forceBlack />
            <PdfLabelValue label="Wall thickening:" value={body.wallThickening ? 'Present' : 'None'} isAbnormal={body.wallThickening} />
            <PdfLabelValue label="Calcification:" value={body.calcification ? 'Present' : 'None'} isAbnormal={body.calcification} />
            <PdfLabelValue label="Mural thrombus:" value={body.muralThrombus ? 'Present' : 'None'} isAbnormal={body.muralThrombus} />

            <PdfAccentSectionHeader title="OUTFLOW VEIN" />
            <PdfLabelValue label="PSV:" value={outflow.psv ? `${outflow.psv} cm/s` : ''} forceBlack />
            <PdfLabelValue label="Stenosis:" value={outflow.stenosisPresent ? 'Present' : 'None'} isAbnormal={outflow.stenosisPresent} />
            {outflow.stenosisPresent && (
              <>
                {outflow.stenosisLocation && <PdfLabelValue label="Stenosis location:" value={outflow.stenosisLocation} isAbnormal />}
                {outflow.stenosisPSV && <PdfLabelValue label="Stenosis PSV:" value={`${outflow.stenosisPSV} cm/s`} isAbnormal />}
              </>
            )}

            {(stenoses.length > 0 || thrombi.length > 0 || aneurysms.length > 0 || complications.perigraftFluidPresent) && (
              <>
                <PdfAccentSectionHeader title="COMPLICATIONS" />
                {stenoses.map((s, i) => (
                  <View key={`sten-${i}`} style={{ marginBottom: 6 }}>
                    <Text style={[commonStyles.label, { marginBottom: 2 }]}>Stenosis {i + 1}:</Text>
                    <PdfLabelValue label="Location:" value={s.location || ''} isAbnormal />
                    <PdfLabelValue label="Severity:" value={s.severity || ''} isAbnormal />
                    {s.grayscale && <PdfLabelValue label="Gray-scale:" value={s.grayscale} isAbnormal />}
                  </View>
                ))}
                {thrombi.map((t, i) => (
                  <View key={`thr-${i}`} style={{ marginBottom: 6 }}>
                    <Text style={[commonStyles.label, { marginBottom: 2 }]}>Thrombus {i + 1}:</Text>
                    <PdfLabelValue label="Location:" value={t.location || ''} isAbnormal />
                    <PdfLabelValue label="Type:" value={t.occlusive || ''} isAbnormal />
                  </View>
                ))}
                {aneurysms.map((a, i) => (
                  <View key={`an-${i}`} style={{ marginBottom: 6 }}>
                    <Text style={[commonStyles.label, { marginBottom: 2 }]}>{a.type || 'Aneurysm'} {i + 1}:</Text>
                    <PdfLabelValue label="Location:" value={a.location || ''} isAbnormal />
                    {a.size && <PdfLabelValue label="Size:" value={`${a.size} cm`} isAbnormal />}
                    <PdfLabelValue label="Contains thrombus:" value={a.thrombus ? 'Yes' : 'No'} isAbnormal={a.thrombus} />
                  </View>
                ))}
                {complications.perigraftFluidPresent && (
                  <View style={{ marginBottom: 6 }}>
                    <Text style={[commonStyles.label, { marginBottom: 2 }]}>Perigraft Fluid:</Text>
                    <PdfLabelValue label="Type:" value={complications.perigraftFluidType || ''} isAbnormal />
                    {complications.perigraftFluidDescription && (
                      <PdfLabelValue label="Description:" value={complications.perigraftFluidDescription} isAbnormal />
                    )}
                  </View>
                )}
              </>
            )}
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
